import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useDispatch } from 'react-redux'

import { resetPhotoViewerAction } from 'actions/photo/photoViewerAction'
import { addPhotosPhotoViewerRatingAction } from 'actions/photo/photoViewerRatingAction'
import { resetPhotoVoteAction } from 'actions/rating/resetPhotoVoteAction'
import { votePhotoNextAction } from 'actions/rating/votePhotoNextAction'
import { showSettingsFilterPhotoAction } from 'actions/settings/showSettingsFilterPhotoAction'
import { KeyboardControl } from 'components/block/KeyboardControl/KeyboardControl'
import { ProfileMain } from 'components/block/Profile/ProfileMain/ProfileMain'
import { GQLForeignRootQuery } from 'components/page/ForeignProfile/gql/__generated__/ForeignRoot'
import { GQLPhotoItemFragment } from 'components/page/ForeignProfile/gql/__generated__/Photos'
import { push } from 'functions/router'
import { useAuthorizationPath } from 'hooks/useAuthorizationLink'
import { useAuthorized } from 'hooks/useAuthorized'
import { useModalOpened } from 'hooks/useModalOpened'

import { convertPhotos } from '../function/convertPhotos'
import { mergePhotos } from '../function/mergePhotos'
import { Direction } from '../Rating.types'
import { DraggableRef, RatingDragDropMemo } from '../RatingDragDrop'

export const RatingProfile: FC<{
  profile: GQLForeignRootQuery['user']
  mainPhoto: GQLPhotoItemFragment
  extraPhotos?: GQLPhotoItemFragment[]
  restPhotos: GQLPhotoItemFragment[]
  onVoteEvent?: Function
  enable: boolean
  ui: GQLForeignRootQuery['ui']
}> = ({
  profile,
  mainPhoto,
  extraPhotos = [],
  restPhotos = [],
  onVoteEvent,
  enable,
  ui,
}) => {
  const dispatch = useDispatch()
  const draggableRef = useRef<DraggableRef>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const authorized = useAuthorized()
  const authorizationPath = useAuthorizationPath()
  const photos = mergePhotos(mainPhoto, extraPhotos, restPhotos)
  const hasModal = useModalOpened()

  const handlePhotoVoteEvent = useCallback(
    (photoId: number, direction: Direction) => {
      if (!authorized) {
        dispatch(push(authorizationPath))
      } else {
        if (!loading) {
          setLoading(true)

          onVoteEvent?.(direction, photoId)

          if (direction > 0) {
            dispatch(showSettingsFilterPhotoAction())
          }

          dispatch(votePhotoNextAction(direction, photoId))

          setLoading(false)
        }
      }
    },
    [onVoteEvent, authorizationPath, authorized, dispatch, loading]
  )

  const rollbackPosition = useMemo(() => {
    if (mainPhoto.voted) {
      if (mainPhoto.liked) {
        return Direction.right
      }
      return Direction.left
    }

    return 0
  }, [mainPhoto])

  const handleEvent = useCallback((action: Direction) => {
    draggableRef.current?.move(action)
  }, [])

  useEffect(() => {
    if (enable) {
      dispatch(resetPhotoViewerAction())
      if (photos.length > 0 && profile) {
        dispatch(
          addPhotosPhotoViewerRatingAction(
            convertPhotos(photos, profile?.id),
            profile.name,
            profile.gender
          )
        )
      }
    }
  }, [dispatch, photos, enable, profile])

  return (
    <RatingDragDropMemo
      ref={draggableRef}
      onHandleEventEnd={(action: Direction) =>
        handlePhotoVoteEvent(mainPhoto.photoId, action)
      }
      onHandleEventRollback={() => dispatch(resetPhotoVoteAction())}
      enable={enable && !hasModal}
      rollbackPosition={rollbackPosition}
    >
      <ProfileMain
        profile={profile}
        photos={photos}
        place="rating"
        ui={ui}
        onHandleClickControlButton={(action) => handleEvent(action)}
        onCallbackComplaint={() => handleEvent(Direction.left)}
        enable={enable}
      />

      {enable && (
        <KeyboardControl onHandleKeyboard={(action) => handleEvent(action)} />
      )}
    </RatingDragDropMemo>
  )
}
